/* ==========================================================================
   EDME — General Product Carousel
   Static kaynak + son gezilenler + sepete son eklenenler
   Dinamik ürünlerde manuel HTML yoktur.
   Tüm dinamik kartlar edme-product-card-renderer section'ından Liquid render edilir.

   PERF NOTU (EDME): Dinamik kartlar artık PARALEL çekiliyor (Promise.all).
   Eskiden her kart sırayla (await + for) çekildiği için 12 ürün ≈ 5 sn
   gecikiyordu. Artık tüm istekler aynı anda başlar; toplam süre tek isteğin
   süresine (~0.4-0.8 sn) iner. Sıralama korunur.
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR = '[data-edme-general-product-carousel]';
  const TRACK_SELECTOR = '[data-edme-gpc-track]';
  const PREV_SELECTOR = '[data-edme-gpc-prev]';
  const NEXT_SELECTOR = '[data-edme-gpc-next]';

  const RECENTLY_VIEWED_KEY = 'edme_recently_viewed_products';
  const RECENTLY_CART_KEY = 'edme_recently_cart_products';

  const DEFAULT_RENDERER_SECTION_ID = 'edme-product-card-renderer';
  const DRAG_THRESHOLD = 12;

  function safeJsonParse(value, fallback) {
    try {
      return JSON.parse(value) || fallback;
    } catch (err) {
      return fallback;
    }
  }

  function getStoredProducts(key) {
    return safeJsonParse(localStorage.getItem(key), []);
  }

  function setStoredProducts(key, products) {
    try {
      localStorage.setItem(key, JSON.stringify(products));
    } catch (err) {}
  }

  function sanitizeProduct(product) {
    if (!product || !product.handle) return null;

    return {
      handle: product.handle,
      url: product.url || `/products/${product.handle}`,
      timestamp: Date.now()
    };
  }

  function pushUniqueProduct(key, rawProduct, maxLength) {
    const product = sanitizeProduct(rawProduct);

    if (!product) return;

    const current = getStoredProducts(key);
    const filtered = current.filter((item) => item && item.handle !== product.handle);
    const next = [product].concat(filtered).slice(0, maxLength || 24);

    setStoredProducts(key, next);
  }

  function saveCurrentProduct(section) {
    const handle = section.getAttribute('data-current-product-handle');

    if (!handle) return;

    const url = section.getAttribute('data-current-product-url') || `/products/${handle}`;

    pushUniqueProduct(
      RECENTLY_VIEWED_KEY,
      {
        handle,
        url
      },
      24
    );
  }

  function getRendererSectionId(section) {
    return section.getAttribute('data-renderer-section-id') || DEFAULT_RENDERER_SECTION_ID;
  }

  async function fetchRenderedProductCard(section, handle) {
    if (!handle) return null;

    const rendererSectionId = getRendererSectionId(section);

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(rendererSectionId)}`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) return null;

      const html = await response.text();
      const template = document.createElement('template');

      template.innerHTML = html.trim();

      const renderedItem =
        template.content.querySelector('.edme-product-card-renderer__item') ||
        template.content.querySelector('[data-edme-product-card-renderer] .edme-product-card-renderer__item');

      if (!renderedItem) return null;

      const item = document.createElement('div');
      item.className = 'edme-general-product-carousel__item';
      item.innerHTML = renderedItem.innerHTML;

      return item;
    } catch (err) {
      console.error('[EDME General Product Carousel] Product card render error:', err);
      return null;
    }
  }

  function getTrack(section) {
    return section.querySelector(TRACK_SELECTOR);
  }

  /* EDME: "Ürün yoksa bölümü hiç gösterme" davranışı.
     Bölüm sunucudan [hidden] + --pending sınıfıyla gelir; ancak gerçekten
     kart basıldığında görünür yapılır. Ayar kapalıysa veya tema editöründe
     isek data-gpc-hide-when-empty hiç basılmaz ve bu fonksiyonlar
     dokunmadan geri döner, yani eski davranış aynen korunur. */
  function hidesWhenEmpty(section) {
    return section.hasAttribute('data-gpc-hide-when-empty');
  }

  function revealSection(section) {
    if (!hidesWhenEmpty(section)) return;
    section.classList.remove('edme-general-product-carousel--pending');
    section.removeAttribute('hidden');
  }

  function getPrevBtn(section) {
    return section.querySelector(PREV_SELECTOR);
  }

  function getNextBtn(section) {
    return section.querySelector(NEXT_SELECTOR);
  }

  function getFirstItem(section) {
    return section.querySelector('.edme-general-product-carousel__item');
  }

  function getFirstProductVisual(section) {
    const firstItem = getFirstItem(section);

    if (!firstItem) return null;

    return (
      firstItem.querySelector('.edme-pcard__media') ||
      firstItem.querySelector('.edme-pcard__image-wrap') ||
      firstItem.querySelector('picture') ||
      firstItem.querySelector('img')
    );
  }

  function syncArrowPosition(section) {
    const visual = getFirstProductVisual(section);

    if (!visual || window.matchMedia('(max-width: 767px)').matches) {
      section.style.removeProperty('--edme-gpc-arrow-top');
      return;
    }

    const visualHeight = visual.getBoundingClientRect().height;

    if (visualHeight > 0) {
      section.style.setProperty('--edme-gpc-arrow-top', `${Math.round(visualHeight / 2)}px`);
    }
  }

  function getScrollAmount(section, track) {
    const card = track ? track.querySelector('.edme-general-product-carousel__item') : null;
    const multiplier = parseInt(section.getAttribute('data-scroll-step'), 10) || 2;

    if (!card) return 300 * multiplier;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;

    return (card.offsetWidth + gap) * multiplier;
  }

  function updateArrows(section) {
    const track = getTrack(section);
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    if (!track || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentLeft = track.scrollLeft;
    const threshold = 4;

    const atStart = currentLeft <= threshold;
    const atEnd = currentLeft >= maxScrollLeft - threshold;
    const noOverflow = maxScrollLeft <= threshold;

    prevBtn.disabled = noOverflow || atStart;
    nextBtn.disabled = noOverflow || atEnd;

    prevBtn.classList.toggle('is-disabled', noOverflow || atStart);
    nextBtn.classList.toggle('is-disabled', noOverflow || atEnd);
  }

  function bindDragScroll(section, track) {
    if (!track || track.dataset.dragBound === 'true') return;

    track.dataset.dragBound = 'true';

    let pointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startLeft = 0;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea')) return;

      pointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      activePointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;

      const diff = event.clientX - startX;

      if (!isDragging && Math.abs(diff) < DRAG_THRESHOLD) {
        return;
      }

      if (!isDragging) {
        isDragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(activePointerId);
      }

      event.preventDefault();
      track.scrollLeft = startLeft - diff;
    });

    const endDrag = (event) => {
      if (!pointerDown) return;

      pointerDown = false;

      if (isDragging) {
        track.dataset.wasDragged = 'true';

        window.setTimeout(() => {
          delete track.dataset.wasDragged;
        }, 0);
      }

      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      try {
        track.releasePointerCapture?.(event.pointerId);
      } catch (err) {}

      window.requestAnimationFrame(() => {
        updateArrows(section);
        syncArrowPosition(section);
      });
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.wasDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bindTrack(section, track) {
    if (!track || track.dataset.scrollBound === 'true') return;

    track.dataset.scrollBound = 'true';

    bindDragScroll(section, track);

    track.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );
  }

  async function renderProductHandles(section, handles) {
    const track = getTrack(section);
    const emptyText = section.getAttribute('data-empty-text') || 'Bu alan için henüz ürün bulunamadı.';

    if (!track) return;

    track.innerHTML = '';

    if (!handles.length) {
      if (!hidesWhenEmpty(section)) {
        track.innerHTML = `<div class="edme-general-product-carousel__empty">${emptyText}</div>`;
      }
      updateArrows(section);
      return;
    }

    // PERF (EDME): Kartları PARALEL çek. Promise.all tüm istekleri aynı anda
    // başlatır; sonuçlar handles dizisinin orijinal sırasıyla geri döner,
    // böylece sıralama korunur. Başarısız/boş olanlar (null) atlanır.
    const cards = await Promise.all(
      handles.map((handle) => fetchRenderedProductCard(section, handle))
    );

    const fragment = document.createDocumentFragment();

    cards.forEach((card) => {
      if (card) {
        fragment.appendChild(card);
      }
    });

    track.innerHTML = '';

    if (!fragment.children.length) {
      if (!hidesWhenEmpty(section)) {
        track.innerHTML = `<div class="edme-general-product-carousel__empty">${emptyText}</div>`;
      }
      updateArrows(section);
      return;
    }

    track.appendChild(fragment);

    // Kartlar DOM'a girdikten SONRA görünür yap. Ok konumu ve genişlik
    // ölçümleri gizli elemanda 0 döneceği için sıralama önemli: önce
    // göster, ölçümler aşağıdaki rAF/timeout'larda yapılsın.
    revealSection(section);

    document.dispatchEvent(
      new CustomEvent('edme:product-cards-rendered', {
        detail: {
          section,
          container: track
        }
      })
    );

    observeImages(section);

    window.requestAnimationFrame(() => {
      updateArrows(section);
      syncArrowPosition(section);
    });

    window.setTimeout(() => {
      updateArrows(section);
      syncArrowPosition(section);
    }, 250);

    window.setTimeout(() => {
      updateArrows(section);
      syncArrowPosition(section);
    }, 700);
  }

  async function hydrateRecentlyViewed(section) {
    const limit = parseInt(section.getAttribute('data-product-limit'), 10) || 12;
    const currentHandle = section.getAttribute('data-current-product-handle') || '';

    const handles = getStoredProducts(RECENTLY_VIEWED_KEY)
      .filter((item) => item && item.handle && item.handle !== currentHandle)
      .map((item) => item.handle)
      .filter(Boolean)
      .slice(0, limit);

    await renderProductHandles(section, handles);
  }

  async function hydrateRecentlyCart(section) {
    const limit = parseInt(section.getAttribute('data-product-limit'), 10) || 12;

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error('Cart fetch failed');

      const cart = await response.json();

      const seen = new Set();

      const products = (cart.items || [])
        .map((item) => ({
          handle: item.handle,
          url: item.url || `/products/${item.handle}`,
          timestamp: Date.now()
        }))
        .filter((item) => {
          if (!item || !item.handle || seen.has(item.handle)) return false;

          seen.add(item.handle);
          return true;
        })
        .slice(0, limit);

      setStoredProducts(RECENTLY_CART_KEY, products);

      const handles = products.map((item) => item.handle);

      await renderProductHandles(section, handles);
    } catch (err) {
      const handles = getStoredProducts(RECENTLY_CART_KEY)
        .filter((item) => item && item.handle)
        .map((item) => item.handle)
        .slice(0, limit);

      await renderProductHandles(section, handles);
    }
  }

  function bindArrows(section) {
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    if (prevBtn && prevBtn.dataset.clickBound !== 'true') {
      prevBtn.dataset.clickBound = 'true';

      prevBtn.addEventListener('click', () => {
        const track = getTrack(section);

        if (!track) return;

        track.scrollBy({
          left: -getScrollAmount(section, track),
          behavior: 'smooth'
        });

        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncArrowPosition(section);
        });
      });
    }

    if (nextBtn && nextBtn.dataset.clickBound !== 'true') {
      nextBtn.dataset.clickBound = 'true';

      nextBtn.addEventListener('click', () => {
        const track = getTrack(section);

        if (!track) return;

        track.scrollBy({
          left: getScrollAmount(section, track),
          behavior: 'smooth'
        });

        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncArrowPosition(section);
        });
      });
    }
  }

  function observeImages(section) {
    const images = section.querySelectorAll('.edme-general-product-carousel__item img');

    images.forEach((img) => {
      if (img.complete) {
        syncArrowPosition(section);
      } else {
        img.addEventListener(
          'load',
          () => {
            syncArrowPosition(section);
          },
          { once: true }
        );
      }
    });
  }

  function initSection(section) {
    if (!section || section.dataset.edmeGeneralProductCarouselInit === 'true') return;

    section.dataset.edmeGeneralProductCarouselInit = 'true';

    const source = section.getAttribute('data-source');
    const track = getTrack(section);

    saveCurrentProduct(section);

    bindTrack(section, track);
    bindArrows(section);
    observeImages(section);

    if (source === 'recently_viewed') {
      hydrateRecentlyViewed(section);
    }

    if (source === 'recently_cart') {
      hydrateRecentlyCart(section);
    }

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncArrowPosition(section);
        });
      },
      { passive: true }
    );

    window.requestAnimationFrame(() => {
      updateArrows(section);
      syncArrowPosition(section);
    });

    window.setTimeout(() => {
      updateArrows(section);
      syncArrowPosition(section);
    }, 300);

    window.setTimeout(() => {
      updateArrows(section);
      syncArrowPosition(section);
    }, 900);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTOR);

    if (section) {
      section.dataset.edmeGeneralProductCarouselInit = 'false';
      initSection(section);
    }
  });
})();